import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2, AlertCircle } from "lucide-react";

const chf = (n) => Number(n || 0).toFixed(2);
const qty = (n) => Number(n || 0).toFixed(0);
const weekLabel = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  const end = new Date(d); end.setDate(d.getDate() + 6);
  const f = (x) => x.toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit" });
  return `${f(d)} – ${f(end)}`;
};

function Totals({ t }) {
  const items = [
    ["Retirées DLC des machines", `${qty(t.withdrawn_qty)} barq.`, `CHF ${chf(t.withdrawn_cost)}`, "dlc-total-withdrawn"],
    ["Détruites / données à Conthey", `${qty(t.final_qty)} barq.`, `CHF ${chf(t.final_cost)}`, "dlc-total-final"],
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {items.map(([label, q, c, tid]) => (
        <div key={tid} className="bg-white border border-[#E5E5E0] rounded-xl p-4" data-testid={tid}>
          <div className="text-xs text-[#575A57]">{label}</div>
          <div className="font-heading text-2xl text-[#1A1C1A]">{c}</div>
          <div className="text-sm text-[#575A57]">{q} · au coût de revient</div>
        </div>
      ))}
    </div>
  );
}

export default function DlcLossesWeekly({ distributors }) {
  const [distributorId, setDistributorId] = useState("");
  const [weeks, setWeeks] = useState("12");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ weeks });
      if (distributorId) params.set("distributor_id", distributorId);
      const { data } = await api.get(`/reports/dlc-losses?${params}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [weeks, distributorId]);

  useEffect(() => { load(); }, [load]);
  const rows = data?.rows || [];

  return (
    <div className="space-y-5" data-testid="dlc-losses-view">
      <div className="bg-white border border-[#E5E5E0] rounded-xl p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="mb-1.5 block text-xs">Distributeur d&apos;origine</Label>
          <Select value={distributorId || "__ALL__"} onValueChange={(v) => setDistributorId(v === "__ALL__" ? "" : v)}>
            <SelectTrigger data-testid="dlc-filter-distributor"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">Tous</SelectItem>
              {distributors.filter((d) => !d.archived).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1.5 block text-xs">Période</Label>
          <Select value={weeks} onValueChange={setWeeks}>
            <SelectTrigger data-testid="dlc-filter-weeks"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["4", "8", "12", "26", "52"].map((w) => <SelectItem key={w} value={w}>{w} dernières semaines</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-[#575A57] self-end">
          Les barquettes détruites ou données à Conthey sont rattachées à la machine d&apos;où elles sont revenues.
          Une même barquette peut apparaître dans les deux colonnes.
        </p>
      </div>

      {error && <div className="text-sm text-[#C05C47] flex gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}
      {loading && <Loader2 className="w-5 h-5 animate-spin text-[#1F4E3D]" />}
      {data && <Totals t={data.totals} />}
      {data?.missing_cost_products?.length > 0 && (
        <p className="text-xs text-[#A04E2C]" data-testid="dlc-missing-cost">
          Coût de revient inconnu (compté à 0) : {data.missing_cost_products.join(", ")}
        </p>
      )}

      <div className="bg-white border border-[#E5E5E0] rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="dlc-losses-table">
          <thead className="bg-[#F5F5F0] text-xs text-[#575A57]">
            <tr>
              <th className="text-left p-3">Semaine</th>
              <th className="text-left p-3">Distributeur</th>
              <th className="text-left p-3">Plat</th>
              <th className="text-right p-3">Retirées DLC</th>
              <th className="text-right p-3">CHF</th>
              <th className="text-right p-3">Détruites / données</th>
              <th className="text-right p-3">CHF</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={7} className="p-6 text-center text-[#575A57]">Aucune perte DLC sur la période.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={`${r.distributor_id}-${r.product_id}-${r.week_start}`} className="border-t border-[#EEEEE8]" data-testid={`dlc-row-${i}`}>
                <td className="p-3 whitespace-nowrap">{weekLabel(r.week_start)}</td>
                <td className="p-3">{r.distributor_name}</td>
                <td className="p-3">{r.product_name}</td>
                <td className="p-3 text-right tabular-nums">{qty(r.withdrawn_qty)}</td>
                <td className="p-3 text-right tabular-nums">{chf(r.withdrawn_cost)}</td>
                <td className="p-3 text-right tabular-nums">{qty(r.final_qty)}</td>
                <td className="p-3 text-right tabular-nums font-medium text-[#A04E2C]">{chf(r.final_cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
